import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../lib/api';
import { subscribeRealtime } from '../lib/realtime';
import type {
  NotificationItem,
  NotificationsListResponse,
  UnreadCountResponse,
} from '../lib/types';

const PAGE_SIZE = 20;

/**
 * Cursor-paginated `/v1/notifications`. Realtime: any user:* event
 * triggers a head-only invalidate so the most recent notification
 * lands without a manual refresh; older pages are insertion-stable
 * (cursor encodes a strictly-older window).
 */
export function useNotifications(unreadOnly = false) {
  const qc = useQueryClient();
  const queryKey = ['notifications', { unreadOnly }] as const;

  const result = useInfiniteQuery<NotificationsListResponse, Error>({
    queryKey,
    initialPageParam: null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (unreadOnly) params.set('unread_only', 'true');
      if (pageParam) params.set('cursor', String(pageParam));
      return api.get<NotificationsListResponse>(
        `/v1/notifications?${params.toString()}`,
      );
    },
    getNextPageParam: (last) => last.next_cursor,
    staleTime: 15_000,
  });

  useEffect(() => {
    const unsub = subscribeRealtime(
      (ch) => ch.endsWith(':notify'),
      () => {
        qc.invalidateQueries({ queryKey });
        qc.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadOnly]);

  return result;
}

export function useUnreadNotificationsCount() {
  const qc = useQueryClient();
  const result = useQuery<UnreadCountResponse, Error>({
    queryKey: ['notifications-unread-count'],
    queryFn: () =>
      api.get<UnreadCountResponse>('/v1/notifications/unread-count'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // Optimistic bump only for realtime envelopes that land a row the
  // in-app feed will actually show. Skip:
  //    - `prefs_changed`: tgbot cache invalidation signal (PR #109)
  //    - `kill_switch`: state-driven via platform_config, no row
  //    - `bot_state`: bot enable/disable toggle (apps/api/.../me.py)
  //    - `config_changed`: webapp-only toast, no producer persists it
  //    - `opened` / `settled` / `cancelled`: trade-lifecycle envelopes.
  //      These persist a row but the api hides trade.* kinds from the
  //      feed + unread-count, so bumping the badge here would inflate
  //      it while the list stays empty — the "1 unread, nothing to
  //      show" UX bug. Telegram still gets them (separate consumer).
  //
  // Without this filter, toggling Settings or an order opening/closing
  // would inflate the badge while the list page stayed empty.
  useEffect(() => {
    const NON_PERSISTED_TYPES = new Set([
      'prefs_changed',
      'kill_switch',
      'bot_state',
      'config_changed',
      'opened',
      'settled',
      'cancelled',
    ]);
    const unsub = subscribeRealtime(
      (ch) => ch.endsWith(':notify'),
      (env) => {
        if (NON_PERSISTED_TYPES.has(env.type)) return;
        qc.setQueryData<UnreadCountResponse>(
          ['notifications-unread-count'],
          (prev) => ({ unread_count: (prev?.unread_count ?? 0) + 1 }),
        );
      },
    );
    return unsub;
  }, [qc]);

  return result;
}

interface MarkReadVars {
  notificationId: string;
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ notificationId }: MarkReadVars) => {
      // The server returns 200 with `{ok: false}` when the row didn't
      // belong to the user, was already read, or was deleted between
      // open + click. React Query treats every fulfilled promise as
      // success → optimistic update sticks even when the row didn't
      // actually flip. Throw to route through onError → rollback.
      // (Codex P2.)
      const res = await api.post<{ ok: boolean }>(
        `/v1/notifications/${notificationId}/read`,
      );
      if (!res.ok) throw new Error('mark-read rejected by server');
      return res;
    },
    onMutate: async ({ notificationId }) => {
      // Optimistic: flip the local read_at and decrement the badge.
      // If the server says ok=false we'll undo on settled.
      const prevList = qc.getQueriesData<{
        pages: NotificationsListResponse[];
      }>({ queryKey: ['notifications'] });
      qc.setQueriesData<{ pages: NotificationsListResponse[] }>(
        { queryKey: ['notifications'] },
        (old) => {
          if (!old?.pages) return old;
          const now = new Date().toISOString();
          return {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              items: p.items.map((it) =>
                it.id === notificationId && !it.read_at
                  ? { ...it, read_at: now }
                  : it,
              ),
            })),
          };
        },
      );
      const prevCount = qc.getQueryData<UnreadCountResponse>([
        'notifications-unread-count',
      ]);
      qc.setQueryData<UnreadCountResponse>(
        ['notifications-unread-count'],
        (prev) => ({
          unread_count: Math.max(0, (prev?.unread_count ?? 0) - 1),
        }),
      );
      return { prevList, prevCount };
    },
    onError: (_err, _vars, ctx) => {
      // Rollback both caches on failure.
      if (ctx?.prevList) {
        for (const [key, val] of ctx.prevList) {
          qc.setQueryData(key, val);
        }
      }
      if (ctx?.prevCount) {
        qc.setQueryData(['notifications-unread-count'], ctx.prevCount);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; updated_count: number }>(
        '/v1/notifications/read-all',
      ),
    onSuccess: () => {
      // Stamp every cached row read + zero the badge. Server is the
      // source of truth so we still invalidate to refetch the precise
      // read_at timestamps.
      qc.setQueriesData<{ pages: NotificationsListResponse[] }>(
        { queryKey: ['notifications'] },
        (old) => {
          if (!old?.pages) return old;
          const now = new Date().toISOString();
          return {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              items: p.items.map((it: NotificationItem) =>
                it.read_at ? it : { ...it, read_at: now },
              ),
            })),
          };
        },
      );
      qc.setQueryData<UnreadCountResponse>(
        ['notifications-unread-count'],
        { unread_count: 0 },
      );
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
